import React from 'react';
import classNames from 'classnames';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';
import styles from '../../styles.module.scss';

const PL_NAME_MAX_LENGTH = 24;
const PL_NUM_MAX_LENGTH = 3;

function parseSubEntra(text) {
  const m = String(text).match(/^([↓↑])\s*(\d+)'\s*Entra:\s*(.+)$/i);
  if (!m) return null;
  return { arrow: m[1], minute: m[2], name: m[3].trim() };
}

function parseSubSale(text) {
  const m = String(text).match(/^([↓↑])\s*(\d+)'\s*Sale:\s*(.+)$/i);
  if (!m) return null;
  return { arrow: m[1], minute: m[2], name: m[3].trim() };
}

function SubEntra({ text }) {
  const p = parseSubEntra(text);
  if (!p) {
    return (
      <span
        className={classNames(
          styles['wca-lineaciones__pl-sub'],
          styles['wca-lineaciones__pl-sub--in'],
        )}
      >
        {text}
      </span>
    );
  }
  return (
    <span className={classNames(styles['wca-lineaciones__pl-sub'], styles['wca-lineaciones__pl-sub--in'])}>
      <span
        className={classNames(
          styles['wca-lineaciones__pl-sub-kick'],
          styles['wca-lineaciones__pl-sub-kick--entra'],
        )}
      >{`${p.arrow} ${p.minute}'`}</span>
      <span className={styles['wca-lineaciones__pl-sub-label']}>Entra:</span>
      <span className={styles['wca-lineaciones__pl-sub-name-in']}> {p.name}</span>
    </span>
  );
}

function SubSale({ text }) {
  const p = parseSubSale(text);
  if (!p) {
    return (
      <span
        className={classNames(
          styles['wca-lineaciones__pl-sub'],
          styles['wca-lineaciones__pl-sub--out'],
        )}
      >
        {text}
      </span>
    );
  }
  return (
    <span className={classNames(styles['wca-lineaciones__pl-sub'], styles['wca-lineaciones__pl-sub--out'])}>
      <span
        className={classNames(
          styles['wca-lineaciones__pl-sub-kick'],
          styles['wca-lineaciones__pl-sub-kick--sale'],
        )}
      >{`${p.arrow} ${p.minute}'`}</span>
      <span className={styles['wca-lineaciones__pl-sub-label']}>Sale:</span>
      <span className={styles['wca-lineaciones__pl-sub-name-out']}> {p.name}</span>
    </span>
  );
}

/** Sustitución: "Entra" (rojo) vs "Sale" (verde); nunca forzar `SubSale` con texto "Entra" (queda en crudo = flecha “blanca”). */
function SubLineFromText({ text, sideHome }) {
  if (!text) return null;
  if (parseSubEntra(text) != null) {
    return <SubEntra text={text} />;
  }
  if (parseSubSale(text) != null) {
    return <SubSale text={text} />;
  }
  return (
    <span
      className={classNames(styles['wca-lineaciones__pl-sub'], {
        [styles['wca-lineaciones__pl-sub--in']]: sideHome,
        [styles['wca-lineaciones__pl-sub--out']]: !sideHome,
      })}
    >
      {text}
    </span>
  );
}

function PlayerEvents({ events, side }) {
  if (!Array.isArray(events) || events.length === 0) return null;
  return (
    <div
      className={classNames(
        styles['wca-lineaciones__pl-events'],
        side === 'home'
          ? styles['wca-lineaciones__pl-events--home']
          : styles['wca-lineaciones__pl-events--away'],
      )}
    >
      {events}
    </div>
  );
}

export default function PlayerRow({
  num,
  numHome,
  numAway,
  nameHome,
  nameAway,
  eventsHome = [],
  eventsAway = [],
  subHome,
  subAway,
  useMobileLayout = false,
}) {
  const hasHomeEvents = Array.isArray(eventsHome) && eventsHome.length > 0;
  const hasAwayEvents = Array.isArray(eventsAway) && eventsAway.length > 0;
  const leftNum = numHome ?? num;
  const rightNum = numAway ?? num;
  const leftNumStr = `${leftNum ?? ''}`;
  const rightNumStr = `${rightNum ?? ''}`;
  const nameHomeStr = `${nameHome ?? ''}`;
  const nameAwayStr = `${nameAway ?? ''}`;

  const homeName = (
    <span className={styles['wca-lineaciones__pl-name']} title={nameHomeStr}>
      {renderMaxLengthText(nameHomeStr, PL_NAME_MAX_LENGTH)}
    </span>
  );
  const awayName = (
    <span className={styles['wca-lineaciones__pl-name']} title={nameAwayStr}>
      {renderMaxLengthText(nameAwayStr, PL_NAME_MAX_LENGTH)}
    </span>
  );

  return (
    <div className={styles['wca-lineaciones__pl-row']}>
      <div
        className={classNames(
          styles['wca-lineaciones__pl-side'],
          styles['wca-lineaciones__pl-side--home'],
        )}
      >
        <span className={styles['wca-lineaciones__pl-num']} title={leftNumStr}>
          {renderMaxLengthText(leftNumStr, PL_NUM_MAX_LENGTH)}
        </span>
        <div className={styles['wca-lineaciones__pl-nameblock']}>
          {useMobileLayout ? (
            homeName
          ) : (
            <div className={styles['wca-lineaciones__pl-nameRow']}>
              {homeName}
              <PlayerEvents events={eventsHome} side="home" />
            </div>
          )}
          {subHome ? <SubLineFromText text={subHome} sideHome /> : null}
        </div>
        {useMobileLayout && hasHomeEvents ? <PlayerEvents events={eventsHome} side="home" /> : null}
      </div>
      <div
        className={classNames(
          styles['wca-lineaciones__pl-side'],
          styles['wca-lineaciones__pl-side--away'],
        )}
      >
        {useMobileLayout && hasAwayEvents ? <PlayerEvents events={eventsAway} side="away" /> : null}
        <div
          className={classNames(
            styles['wca-lineaciones__pl-nameblock'],
            styles['wca-lineaciones__pl-nameblock--away'],
          )}
        >
          {useMobileLayout ? (
            awayName
          ) : (
            <div
              className={classNames(
                styles['wca-lineaciones__pl-nameRow'],
                styles['wca-lineaciones__pl-nameRow--away'],
              )}
            >
              <PlayerEvents events={eventsAway} side="away" />
              {awayName}
            </div>
          )}
          {subAway ? <SubLineFromText text={subAway} sideHome={false} /> : null}
        </div>
        <span className={styles['wca-lineaciones__pl-num']} title={rightNumStr}>
          {renderMaxLengthText(rightNumStr, PL_NUM_MAX_LENGTH)}
        </span>
      </div>
    </div>
  );
}
